import * as React from 'react';
import { useMemo } from 'react';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import {
  TableWrapper,
  TableCell,
  TableCellSmall,
  TableCellNoPad,
  TableBody,
  TableHeader,
  HeaderCell,
  HeaderCellWrapper,
  CallPutText,
  MutedText,
} from 'components/Table';
import { HeatScore } from 'components/Score';
import OptionFlowTradeSide from 'components/OptionFlowTradeSide/OptionFlowTradeSide';
import OptionFlowTradeRead from 'components/OptionFlowTradeSide/OptionFlowTradeRead';
import Pulse from 'components/Loading/Pulse';
import { DashboardRow } from 'screens/TickerResearchPage/styles';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { getOptionFlowRowSentiment } from 'utils/shared/trades/getOptionFlowRowSentiment';
import { OptionsFlowTableProps } from './types';

const OptionsFlowTable: React.FC<OptionsFlowTableProps> = ({ trades, loading }) => {
  const top10Trades = useMemo(() => trades.slice(0, 10), [trades]);

  if (loading || top10Trades.length === 0) {
    return (
      <div className="flex items-center justify-center bg-palette-black-2 p-8">
        <Pulse />
      </div>
    );
  }

  return (
    <TableWrapper className="grid">
      <TableHeader>
        <DashboardRow removePad>
          <HeaderCellWrapper>
            <HeaderCell>Date</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Time</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>C/P</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Side</HeaderCell>
          </HeaderCellWrapper>
          {ENABLE_OPTION_FLOW_READ_MODE && (
            <HeaderCellWrapper>
              <HeaderCell>Read</HeaderCell>
            </HeaderCellWrapper>
          )}
          <HeaderCellWrapper>
            <HeaderCell>Expiry</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Spot</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Strike</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>OTM</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Price</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Size</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Open Interest</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Implied Vol</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Type</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper>
            <HeaderCell>Premium</HeaderCell>
          </HeaderCellWrapper>
          <HeaderCellWrapper removePad>
            <HeaderCell>Heat Score</HeaderCell>
          </HeaderCellWrapper>
        </DashboardRow>
      </TableHeader>
      <TableBody animateRows className="h-auto w-full">
        <div className="w-full overflow-x-auto">
          {top10Trades.map((trade, index) => {
            const sentiment = getOptionFlowRowSentiment(trade);

            return (
              <DashboardRow
                key={trade.id || index}
                tableBodyRow
                isUnusual={trade.isUnusual}
                isGoldenSweep={trade.isGoldenSweep}
              >
                <TableCell>
                  <MutedText>{trade.date}</MutedText>
                </TableCell>
                <TableCell>
                  <MutedText>
                    {trade.time} {trade.timeSubscript}
                  </MutedText>
                </TableCell>
                <TableCell>
                  <CallPutText type={trade.cp || ''}>{trade.cp}</CallPutText>
                </TableCell>
                <TableCell>
                  <OptionFlowTradeSide trade={trade} />
                </TableCell>
                {ENABLE_OPTION_FLOW_READ_MODE && (
                  <TableCell className="items-center">
                    <OptionFlowTradeRead trade={trade} />
                  </TableCell>
                )}
                <TableCell>{`${trade.expireYear}-${trade.expireMonth}-${trade.expireDay}`}</TableCell>
                <TableCell>${trade.spot.toFixed(2)}</TableCell>
                <TableCell>${trade.strike}</TableCell>
                <TableCell>{Math.round(trade.otmPercent)}%</TableCell>
                <TableCell>${trade.price.toFixed(2)}</TableCell>
                <TableCell>{trade.size?.toLocaleString()}</TableCell>
                <TableCell>{trade.openInterest?.toLocaleString()}</TableCell>
                <TableCell>{Math.round(trade.impliedVol * 100)}%</TableCell>
                <TableCellSmall>{trade.orderType}</TableCellSmall>
                <TableCell>
                  <CallPutText type={sentiment}>${abbreviateNumber(trade.premium)}</CallPutText>
                </TableCell>
                <TableCellNoPad>
                  <HeatScore scoreWidth={trade.scoreWidth} />
                </TableCellNoPad>
              </DashboardRow>
            );
          })}
        </div>
      </TableBody>
    </TableWrapper>
  );
};

export default OptionsFlowTable;
